import React, { useEffect, useState } from "react";
import { api, buildImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, Upload } from "lucide-react";

export default function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/media"); setItems(data); }
    catch { /* endpoint may be new */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      await load();
    } catch { toast.error("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  };

  const copy = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success("URL copied"));
  };

  return (
    <div className="space-y-4" data-testid="media-library">
      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-base font-semibold text-[#0B132B]">Media Library</h3>
          <p className="text-xs text-slate-500 mt-0.5">Upload images once, reuse them anywhere. Copy URL to paste into a product.</p>
        </div>
        <label>
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="media-upload-input" />
          <Button asChild disabled={uploading} className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0]" data-testid="media-upload-btn">
            <span><Upload className="w-4 h-4 me-2" />{uploading ? "Uploading…" : "Upload Image"}</span>
          </Button>
        </label>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center p-8 border border-dashed border-slate-300 rounded-2xl">No media uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {items.map((m) => {
            const url = m.url?.startsWith("http") ? m.url : buildImageUrl(m.storage_path || m.url || "");
            return (
              <div key={m.id || m.storage_path} className="bg-white border border-slate-200 rounded-2xl overflow-hidden group" data-testid={`media-item-${m.id}`}>
                <div className="bg-slate-50 aspect-square flex items-center justify-center p-3">
                  <img src={url} alt="" loading="lazy" className="max-h-full max-w-full object-contain" />
                </div>
                <button onClick={() => copy(url)} className="w-full py-2 text-xs text-slate-600 border-t border-slate-100 hover:bg-slate-50 flex items-center justify-center gap-1" data-testid={`media-copy-${m.id}`}>
                  <Copy className="w-3 h-3" /> Copy URL
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
